import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { PatientRegistrationRequestDto } from '@/api/proxy/mpi/models/patient-registration-request-dto';
import { environment } from '../../../environments/default/environment';

/**
 * What MPI's POST /Book/Complete/Demo/{id} returns instead of posting to Elixir Live.
 */
export interface DemoSubmissionResult {
  /** The exact PatientRegistrationRequest MPI would have posted to Elixir Live. */
  payload: unknown;
  /** Sample PDF as base64 (no data: prefix). */
  pdf: string | null;
}

/**
 * Staging demo mode (environment.enableDemo). The registration itself is created by ELive as normal
 * against the demo tenant; only the submission differs, going to the demo complete endpoint, which
 * returns the Elixir payload and a PDF instead of posting to Elixir Live.
 */
@Injectable({ providedIn: 'root' })
export class DemoService {
  private readonly RESULT_STORAGE_KEY = 'demo_submission_result';
  private mpiRootUrl = environment.mpiBaseUrl;
  private current: DemoSubmissionResult | null = null;

  constructor(private http: HttpClient) {}

  get isEnabled(): boolean {
    return environment.enableDemo === true;
  }

  /**
   * Completes the registration against the hidden demo endpoint, which skips the Elixir Live post and
   * returns the payload and PDF instead. It is not on swagger, so there is no generated client for it.
   */
  async completeRegistration(bookRegistrationId: string, body: PatientRegistrationRequestDto): Promise<DemoSubmissionResult | null> {
    const response = await firstValueFrom(
      this.http.post<DemoSubmissionResult | null>(`${this.mpiRootUrl}/Book/Complete/Demo/${encodeURIComponent(bookRegistrationId)}`, body)
    );

    const result = response && typeof response === 'object' && 'payload' in response ? response : null;
    this.setResult(result);
    return result;
  }

  /** In-memory first; session storage lets the results view survive a page refresh. */
  getResult(): DemoSubmissionResult | null {
    if (this.current) return this.current;
    try {
      const stored = sessionStorage.getItem(this.RESULT_STORAGE_KEY);
      return stored ? (JSON.parse(stored) as DemoSubmissionResult) : null;
    } catch {
      return null;
    }
  }

  clearResult(): void {
    this.setResult(null);
  }

  private setResult(result: DemoSubmissionResult | null): void {
    this.current = result;
    try {
      if (!result) {
        sessionStorage.removeItem(this.RESULT_STORAGE_KEY);
        return;
      }
      try {
        sessionStorage.setItem(this.RESULT_STORAGE_KEY, JSON.stringify(result));
      } catch {
        // A large PDF can exceed the storage quota; keep the payload so a refresh still shows it.
        sessionStorage.setItem(this.RESULT_STORAGE_KEY, JSON.stringify({ ...result, pdf: null }));
      }
    } catch {
      // Storage unavailable (e.g. blocked); the in-memory result is still used.
    }
  }
}
